import {
  Component,
  EventEmitter,
  Input,
  NgZone,
  OnInit,
  Output,
} from '@angular/core';
import { getDefaultDispoPermissions } from 'server/helpers/permissions/permissions';
import { CustomerTypeEnum } from 'server/shared/models/customer';
import {
  DispoPermission,
  DispoPermissions,
} from 'server/shared/models/dispoPermissions';
import { RoleEnum } from 'server/shared/models/role.enum';
import { User } from 'server/shared/models/user';
import { PermissionsService } from '../../../services/permissions.service';
interface FeaturePermission {
  translationString: string;
  value: string;
  type?:
    | 'dispoAccess'
    | 'poolerAccess'
    | 'requesterAccess'
    | 'managePermissions'
    | 'transferPermissions'
    | 'sharedPoolPermissions'
    | 'isCustomerAdmin'
    | 'hasJobAccess'
    | 'hasEmploymentAccess'
    | 'hasRequestsAccess'
    | 'canApproveJobRequests'
    | 'canApproveHours'
    | 'manageTranscribeJobListPermissions';
  checked: boolean;
  disabled?: boolean;
  visible?: boolean;
  subPermission?: FeaturePermission[];
}

@Component({
  selector: 'app-feature-permission',
  templateUrl: './feature-permission.component.html',
  styleUrls: ['./feature-permission.component.scss'],
})
export class FeaturePermissionComponent implements OnInit {
  user?: User;
  @Input()
  set profileUser(user: User) {
    this.user = user;
    this.updateState();
    this.checkUserHasPermissions();
  }

  @Input() customerType?: CustomerTypeEnum;

  @Input() isAdminPermissions: boolean = false;

  @Input() hasJobs: boolean = false;

  @Input() hasEmployments: boolean = false;

  @Input() hasRequestProcess: boolean = false;

  @Input() hasTimeTracking: boolean = false;

  @Output() permissionChanged = new EventEmitter<DispoPermissions>();

  featurePermissions: FeaturePermission[] = [
    {
      translationString: 'canPooler',
      type: 'poolerAccess',
      value: 'poolerAccess',
      checked: false,
      disabled: true,
      visible: true,
      subPermission: [
        {
          translationString: 'read',
          value: 'read',
          checked: false,
          disabled: true,
        },
        {
          translationString: 'create',
          value: 'create',
          checked: false,
        },
        {
          translationString: 'update',
          value: 'update',
          checked: false,
        },
        {
          translationString: 'delete',
          value: 'delete',
          checked: false,
        },
      ],
    },
    {
      translationString: 'canRequester',
      type: 'requesterAccess',
      value: 'requesterAccess',
      checked: false,
      disabled: false,
      visible: true,
      subPermission: [
        {
          translationString: 'read',
          value: 'read',
          checked: false,
          disabled: false,
        },
        {
          translationString: 'create',
          value: 'create',
          checked: false,
        },
        {
          translationString: 'update',
          value: 'update',
          checked: false,
        },
        {
          translationString: 'delete',
          value: 'delete',
          checked: false,
        },
      ],
    },
    {
      translationString: 'canDispos',
      value: 'dispoAccess',
      type: 'dispoAccess',
      checked: false,
      disabled: false,
      visible: true,
      subPermission: [
        {
          translationString: 'read',
          value: 'read',
          checked: false,
        },
        {
          translationString: 'create',
          value: 'create',
          checked: false,
        },
        {
          translationString: 'update',
          value: 'update',
          checked: false,
        },
        {
          translationString: 'delete',
          value: 'delete',
          checked: false,
        },
      ],
    },
    {
      translationString: 'canPermission',
      type: 'managePermissions',
      value: 'managePermissions',
      checked: false,
      disabled: true,
      visible: true,
      subPermission: [
        {
          translationString: 'read',
          value: 'read',
          checked: false,
        },
        {
          translationString: 'update',
          value: 'update',
          checked: false,
        },
      ],
    },
    {
      translationString: 'canManageTranscribeJobList',
      type: 'manageTranscribeJobListPermissions',
      value: 'manageTranscribeJobListPermissions',
      checked: false,
      disabled: false,
      visible: true,
      subPermission: [
        {
          translationString: 'read',
          value: 'read',
          checked: true,
        },
        {
          translationString: 'update',
          value: 'update',
          checked: true,
        },
      ],
    },
    {
      translationString: 'canTransfers',
      type: 'transferPermissions',
      value: 'transferPermissions',
      checked: true,
      disabled: false,
      visible: true,
    },
    {
      translationString: 'canSharedPool',
      type: 'sharedPoolPermissions',
      value: 'sharedPoolPermissions',
      checked: true,
      disabled: false,
      visible: true,
    },
    {
      translationString: 'canCustomerAdmin',
      type: 'isCustomerAdmin',
      value: 'isCustomerAdmin',
      checked: false,
      disabled: true,
      visible: false,
    },
    {
      translationString: 'canJobAccess',
      type: 'hasJobAccess',
      value: 'hasJobAccess',
      checked: false,
      disabled: true,
      visible: false,
    },
    {
      translationString: 'canEmploymentAccess',
      type: 'hasEmploymentAccess',
      value: 'hasEmploymentAccess',
      checked: false,
      disabled: true,
      visible: false,
    },
    {
      translationString: 'canRequestsAccess',
      type: 'hasRequestsAccess',
      value: 'hasRequestsAccess',
      checked: false,
      disabled: true,
      visible: false,
    },
    {
      translationString: 'canApproveJobRequests',
      type: 'canApproveJobRequests',
      value: 'canApproveJobRequests',
      checked: false,
      disabled: false,
      visible: true,
    },
    {
      translationString: 'canApproveHours',
      type: 'canApproveHours',
      value: 'canApproveHours',
      checked: false,
      disabled: false,
      visible: false,
    },
  ];

  constructor(
    public permissionsService: PermissionsService,
    private ngZone: NgZone,
  ) {}

  ngOnInit(): void {
    this.updateState();
    this.checkUserHasPermissions();
  }

  isEditingOwnUser(): boolean {
    return this.permissionsService?.loggedInUser?._id === this.user?._id;
  }

  checkUserHasPermissions() {
    this.featurePermissions.forEach((permission) => {
      const canManagePermissions =
        this.permissionsService.canUpdateManagePermissions();
      permission.visible = this.permissionsService.canReadManagePermissions();
      permission.disabled = !canManagePermissions;

      if (this.isAdminPermissions) {
        if (
          permission.type === 'dispoAccess' ||
          permission.type === 'poolerAccess' ||
          permission.type === 'requesterAccess' ||
          permission.type === 'transferPermissions' ||
          permission.type === 'sharedPoolPermissions' ||
          permission.type === 'hasJobAccess' ||
          permission.type === 'hasEmploymentAccess' ||
          permission.type === 'hasRequestsAccess' ||
          permission.type === 'manageTranscribeJobListPermissions' ||
          permission.type === 'canApproveJobRequests'
        ) {
          permission.disabled = false;
          permission.visible = false;
        }
      } else {
        if (
          permission.type === 'managePermissions' ||
          permission.type === 'isCustomerAdmin'
        ) {
          permission.disabled = false;
          permission.visible = false;
        }
      }

      if (!this.hasJobs) {
        if (permission.type === 'hasJobAccess') {
          permission.disabled = false;
          permission.visible = false;
        }
      }

      if (!this.hasEmployments) {
        if (permission.type === 'hasEmploymentAccess') {
          permission.disabled = false;
          permission.visible = false;
        }
      }

      if (!this.hasRequestProcess) {
        if (permission.type === 'requesterAccess') {
          permission.disabled = false;
          permission.visible = false;
        }
        if (permission.type === 'hasRequestsAccess') {
          permission.disabled = false;
          permission.visible = false;
        }
        if (permission.type === 'canApproveJobRequests') {
          permission.disabled = false;
          permission.visible = false;
        }
      }

      if (!this.hasTimeTracking) {
        if (permission.type === 'canApproveHours') {
          permission.disabled = false;
          permission.visible = false;
        }
      } else {
        if (permission.type === 'canApproveHours') {
          permission.disabled = !canManagePermissions;
          permission.visible = true;
        }
      }

      if (this.customerType === CustomerTypeEnum.AGENCY) {
        if (permission.type === 'transferPermissions') {
          permission.disabled = false;
          permission.visible = false;
        }
        if (permission.type === 'sharedPoolPermissions') {
          permission.disabled = false;
          permission.visible = false;
        }
      }

      permission.subPermission?.forEach((subPermission) => {
        subPermission.disabled =
          !canManagePermissions || subPermission.disabled;
        if (
          this.permissionsService.loggedInUser?.role === RoleEnum.DISPO &&
          this.permissionsService.loggedInUser.isAdmin
        ) {
          subPermission.disabled = false;
        }
      });
    });
  }

  updatePermissionAccess(
    permission: FeaturePermission,
    type: keyof Omit<DispoPermissions, 'departments'>,
  ) {
    permission.subPermission?.forEach((subPermission) => {
      subPermission.checked =
        this.user?.role === RoleEnum.DISPO &&
        //    @ts-ignore
        this.user?.dispoPermissions?.[type]?.[subPermission.value] !== undefined
          ? //  @ts-ignore
            this.user?.dispoPermissions?.[type]?.[subPermission.value]
          : //  @ts-ignore
            getDefaultDispoPermissions()[type][subPermission.value];
    });
  }

  updateFeaturePermission(
    permission: FeaturePermission,
    type: keyof NonNullable<DispoPermission['featurePermissions']>,
  ) {
    if (permission.subPermission?.length) {
      //read
      permission.subPermission?.forEach((subPermission) => {
        subPermission.checked =
          this.user?.role === RoleEnum.DISPO &&
          //   @ts-ignore
          this.user?.dispoPermissions?.dispoAccess?.featurePermissions?.[
            type
          ]?.[subPermission.value] !== undefined
            ? //   @ts-ignore
              this.user?.dispoPermissions?.dispoAccess?.featurePermissions?.[
                type
              ]?.[subPermission.value]
            : //   @ts-ignore
              getDefaultDispoPermissions().dispoAccess.featurePermissions[type][
                subPermission.value
              ];
      });
    } else {
      permission.checked = !!(this.user?.role === RoleEnum.DISPO &&
      this.user?.dispoPermissions?.dispoAccess?.featurePermissions?.[type] !==
        undefined
        ? this.user.dispoPermissions.dispoAccess.featurePermissions[type]
        : getDefaultDispoPermissions().dispoAccess.featurePermissions[type]);
    }
  }

  updateState() {
    this.featurePermissions.forEach((permission) => {
      if (permission.type === 'dispoAccess') {
        this.updatePermissionAccess(permission, 'dispoAccess');
      } else if (permission.type === 'poolerAccess') {
        this.updatePermissionAccess(permission, 'poolerAccess');
      } else if (permission.type === 'requesterAccess') {
        this.updatePermissionAccess(permission, 'requesterAccess');
      } else if (permission.type === 'managePermissions') {
        this.updateFeaturePermission(permission, 'managePermissions');
      } else if (permission.type === 'manageTranscribeJobListPermissions') {
        this.updateFeaturePermission(
          permission,
          'manageTranscribeJobListPermissions',
        );
      } else if (permission.type === 'transferPermissions') {
        this.updateFeaturePermission(permission, 'transferPermissions');
      } else if (permission.type === 'sharedPoolPermissions') {
        this.updateFeaturePermission(permission, 'sharedPoolPermissions');
      } else if (permission.type === 'hasJobAccess') {
        this.updateFeaturePermission(permission, 'hasJobAccess');
      } else if (permission.type === 'hasEmploymentAccess') {
        this.updateFeaturePermission(permission, 'hasEmploymentAccess');
      } else if (permission.type === 'hasRequestsAccess') {
        this.updateFeaturePermission(permission, 'hasRequestsAccess');
      } else if (permission.type === 'isCustomerAdmin') {
        this.updateFeaturePermission(permission, 'isCustomerAdmin');
      } else if (permission.type === 'canApproveJobRequests') {
        this.updateFeaturePermission(permission, 'canApproveJobRequests');
      } else if (permission.type === 'canApproveHours') {
        this.updateFeaturePermission(permission, 'canApproveHours');
      }
      permission.checked =
        permission.subPermission?.every((s) => s.checked) || permission.checked;
    });
  }

  emitChanges() {
    let dispoPermission: DispoPermissions =
      this.user?.role === RoleEnum.DISPO && this.user.dispoPermissions
        ? this.user.dispoPermissions
        : getDefaultDispoPermissions();
    dispoPermission = JSON.parse(JSON.stringify(dispoPermission));

    let featurePermissions = {};

    function setPermissions(
      permission: FeaturePermission,
      type: keyof Omit<DispoPermissions, 'departments'>,
    ) {
      if (permission.checked) {
        dispoPermission[type] = {
          read: true,
          create: true,
          update: true,
          delete: true,
        };
      } else {
        let newPermission = {};
        permission.subPermission?.forEach((subPermission) => {
          //  @ts-ignore
          newPermission[subPermission.value] = subPermission.checked;
        });
        dispoPermission[type] = newPermission;
      }
    }

    function setFeaturePermissions(
      permission: FeaturePermission,
      // @ts-ignore
      type: keyof DispoPermissions['dispoAccess']['featurePermissions'],
    ) {
      if (permission.checked) {
        // @ts-ignore
        if (permission.subPermission?.length) {
          // @ts-ignore
          featurePermissions[type] = {
            read: true,
            update: true,
          };
        } else {
          // @ts-ignore
          featurePermissions[type] = true;
        }
      } else if (permission.subPermission?.length) {
        let newPermission = {};
        permission.subPermission?.forEach((subPermission) => {
          //  @ts-ignore
          newPermission[subPermission.value] = subPermission.checked;
        });
        // @ts-ignore
        featurePermissions[type] = newPermission;
      } else {
        // @ts-ignore
        featurePermissions[type] = false;
      }
    }

    this.featurePermissions.forEach((permission) => {
      if (permission.type === 'dispoAccess') {
        setPermissions(permission, 'dispoAccess');
      }
      if (permission.type === 'poolerAccess') {
        setPermissions(permission, 'poolerAccess');
      }
      if (permission.type === 'requesterAccess') {
        setPermissions(permission, 'requesterAccess');
      }
      // Admin user can give permission to any dispo user, if they can see & update DispoAccess Permission
      if (permission.type === 'managePermissions') {
        setFeaturePermissions(permission, 'managePermissions');
      }
      if (permission.type === 'manageTranscribeJobListPermissions') {
        setFeaturePermissions(permission, 'manageTranscribeJobListPermissions');
      }
      if (permission.type === 'transferPermissions') {
        setFeaturePermissions(permission, 'transferPermissions');
      }
      if (permission.type === 'sharedPoolPermissions') {
        setFeaturePermissions(permission, 'sharedPoolPermissions');
      }
      if (permission.type === 'hasJobAccess') {
        setFeaturePermissions(permission, 'hasJobAccess');
      }
      if (permission.type === 'hasEmploymentAccess') {
        setFeaturePermissions(permission, 'hasEmploymentAccess');
      }
      if (permission.type === 'hasRequestsAccess') {
        setFeaturePermissions(permission, 'hasRequestsAccess');
      }
      if (permission.type === 'isCustomerAdmin') {
        setFeaturePermissions(permission, 'isCustomerAdmin');
      }
      if (permission.type === 'canApproveJobRequests') {
        setFeaturePermissions(permission, 'canApproveJobRequests');
      }
      if (permission.type === 'canApproveHours') {
        setFeaturePermissions(permission, 'canApproveHours');
      }
    });

    if (!dispoPermission.dispoAccess) {
      dispoPermission.dispoAccess = {};
    }
    dispoPermission.dispoAccess.featurePermissions = featurePermissions;
    this.permissionChanged.emit(dispoPermission);
  }

  updateAllComplete(permission: FeaturePermission) {
    permission.checked =
      permission.subPermission != null &&
      permission.subPermission?.every((p) => p.checked);
    this.emitChanges();
  }

  someComplete(permission: FeaturePermission): boolean {
    if (permission.subPermission == null) {
      return false;
    }
    return (
      permission.subPermission.filter((t) => t.checked).length > 0 &&
      !permission.checked
    );
  }

  setAll(completed: boolean, permission: FeaturePermission) {
    permission.checked = completed;
    if (!permission.subPermission?.length) {
      this.emitChanges();
      return;
    }
    permission.subPermission.forEach((t) => (t.checked = completed));
    this.emitChanges();
  }
}
